<template>
    <b-container fluid class="my-3">
        <validation-observer ref="form" v-slot="{ handleSubmit, meta }" :initial-values="values">
            <b-form @submit.prevent="handleSubmit($event, onFormSubmit)">
                <busy-modal :show="loading" :overlay="true"></busy-modal>
                <b-row>
                    <b-col md="6">
                        <validation-provider name="email" v-slot="{ errors, handleChange }" rules="email">
                            <form-input
                                floating-label="Email"
                                type="email"
                                v-model="values.email"
                                :left-icon="['fas', 'envelope']"
                                :error="errors[0]"
                                @change="handleChange"
                            ></form-input>
                        </validation-provider>
                    </b-col>
                    <b-col md="6">
                        <validation-provider name="company" v-slot="{ errors, handleChange }" rules="required">
                            <form-input
                                floating-label="Company"
                                type="text"
                                :left-icon="['fas', 'building']"
                                v-model="values.company"
                                :error="errors[0]"
                                @change="handleChange"
                            ></form-input>
                        </validation-provider>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col md="6">
                        <validation-provider name="first_name" v-slot="{ errors }">
                            <form-input
                                floating-label="First Name"
                                v-model="values.first_name"
                                :left-icon="['fas', 'user']"
                                :error="errors[0]"
                            ></form-input>
                        </validation-provider>
                    </b-col>
                    <b-col md="6">
                        <validation-provider name="last_name" v-slot="{ errors }">
                            <form-input
                                floating-label="Last Name"
                                v-model="values.last_name"
                                :left-icon="['fas', 'user']"
                                :error="errors[0]"
                            ></form-input>
                        </validation-provider>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col md="6">
                        <validation-provider name="specialty" v-slot="{ errors }">
                            <form-input
                                floating-label="Specialty"
                                v-model="values.specialty"
                                :left-icon="['fas', 'id-badge']"
                                :error="errors[0]"
                            ></form-input>
                        </validation-provider>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col md="6">
                        <validation-provider name="address.address1" v-slot="{ errors }">
                            <form-input
                                floating-label="Address1"
                                v-model="values.address.address1"
                                :left-icon="['fas', 'location-dot']"
                                :error="errors[0]"
                            ></form-input>
                        </validation-provider>
                    </b-col>
                    <b-col md="6">
                        <validation-provider name="address.address2" v-slot="{ errors }">
                            <form-input
                                floating-label="Address2"
                                v-model="values.address.address2"
                                :left-icon="['fas', 'location-dot']"
                                :error="errors[0]"
                            ></form-input>
                        </validation-provider>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col md="4">
                        <validation-provider name="address.country" v-slot="{ errors }">
                            <form-select
                                floating-label="Country"
                                v-model="values.address.country"
                                :left-icon="['fas', 'location-dot']"
                                :options="countryOptions"
                                :error="errors[0]"
                            ></form-select>
                        </validation-provider>
                    </b-col>
                    <b-col md="2">
                        <validation-provider name="address.zip" v-slot="{ errors }">
                            <form-input
                                floating-label="Zip"
                                v-model="values.address.zip"
                                :left-icon="['fas', 'location-dot']"
                                :error="errors[0]"
                                @update="handleZipChange"
                            ></form-input>
                        </validation-provider>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col md="6">
                        <validation-provider name="address.city" v-slot="{ errors }">
                            <form-input
                                floating-label="City"
                                v-model="values.address.city"
                                :left-icon="['fas', 'location-dot']"
                                :error="errors[0]"
                            ></form-input>
                        </validation-provider>
                    </b-col>
                    <b-col md="6">
                        <validation-provider name="address.state" v-slot="{ errors }">
                            <form-select
                                floating-label="State/Region"
                                v-model="values.address.state"
                                :left-icon="['fas', 'location-dot']"
                                :options="stateOptions"
                                :error="errors[0]"
                            ></form-select>
                        </validation-provider>
                    </b-col>
                </b-row>
                <b-row v-if="provinces.length > 0">
                    <b-col md="6">
                        <validation-provider name="address.province" v-slot="{ errors }">
                            <form-select
                                floating-label="Province"
                                v-model="values.address.province"
                                :left-icon="['fas', 'location-dot']"
                                :options="provinceOptions"
                                :error="errors[0]"
                            ></form-select>
                        </validation-provider>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col md="6">
                        <validation-provider name="mobile_phone" v-slot="{ errors }">
                            <form-phone-number-input
                                inputId="vendor-phone-mobile"
                                floating-label="Phone (mobile)"
                                v-model="values.mobile_phone"
                                :default-country-code="defaultCountryCode"
                                :error="errors[0]"
                            ></form-phone-number-input>
                        </validation-provider>
                    </b-col>
                    <b-col md="6">
                        <validation-provider name="address.phone" v-slot="{ errors }">
                            <form-phone-number-input
                                inputId="vendor-phone-home"
                                floating-label="Phone (home)"
                                v-model="values.address.phone"
                                :default-country-code="defaultCountryCode"
                                :error="errors[0]"
                            ></form-phone-number-input>
                        </validation-provider>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col md="4">
                        <validation-provider name="address.work_phone" v-slot="{ errors }">
                            <form-phone-number-input
                                inputId="vendor-phone-work"
                                floating-label="Phone (work)"
                                v-model="values.address.work_phone"
                                :default-country-code="defaultCountryCode"
                                :error="errors[0]"
                            ></form-phone-number-input>
                        </validation-provider>
                    </b-col>
                    <b-col md="2">
                        <validation-provider
                            name="address.work_phone_ext"
                            v-slot="{ errors, handleChange }"
                            rules="maxMessageLen:6,Ext"
                        >
                            <form-input
                                floating-label="Ext"
                                v-model="values.address.work_phone_ext"
                                :error="errors[0]"
                                @change="handleChange"
                            ></form-input>
                        </validation-provider>
                    </b-col>
                    <b-col md="6">
                        <validation-provider name="address.fax" v-slot="{ errors }">
                            <form-phone-number-input
                                inputId="vendor-fax-number"
                                floating-label="Fax"
                                v-model="values.address.fax"
                                :default-country-code="defaultCountryCode"
                                :error="errors[0]"
                            ></form-phone-number-input>
                        </validation-provider>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col lg="6">
                        <validation-provider name="note" v-slot="{ errors }">
                            <form-textarea
                                v-model="values.note"
                                floating-label="Notes"
                                :left-icon="['fas', 'note-sticky']"
                                :error="errors[0]"
                                rows="7"
                            />
                        </validation-provider>
                    </b-col>
                </b-row>
                <div class="pb-safari-only">
                    <responsive-button :icon="['fas', 'arrow-left']" @click="goToVendorManager" variant="primary"
                        >Back to Vendor Manager</responsive-button
                    >
                    <b-button class="mt-4 mb-4" type="submit" variant="primary" :disabled="loading || !meta.valid">
                        Save
                    </b-button>
                </div>
            </b-form>
        </validation-observer>
    </b-container>
</template>

<script>
    import API from '../../../api';
    import isEmpty from 'lodash/isEmpty';
    import get from 'lodash/get';
    import { mapGetters, mapActions } from 'vuex';
    import VendorManagerEventBus from './vendor-manager-event-bus.vue';
    import { formatResponseError } from '../../../utils/api';
    import BusyModal from '../../generic/busy-modal/busy-modal.vue';
    import FormInput from '../../generic/form-input/form-input.vue';
    import FormSelect from '../../generic/form-select/form-select.vue';
    import FormTextarea from '../../generic/form-textarea/form-textarea.vue';
    import ResponsiveButton from '../../generic/responsive-button/responsive-button.vue';
    import { defineAsyncComponent } from 'vue';

    const FormPhoneNumberInput = defineAsyncComponent(() =>
        import(
            /* webpackChunkName: "lx-form-phone-number-input" */
            '../../generic/form-phone-number-input/form-phone-number-input.vue'
        )
    );

    export default {
        name: 'WorkOrdersVendorManagerForm',
        components: {
            BusyModal,
            FormInput,
            FormPhoneNumberInput,
            FormSelect,
            FormTextarea,
            ResponsiveButton,
        },
        computed: {
            ...mapGetters('owner', ['metadata']),
            ...mapGetters('workOrders', ['vendor']),
            defaultCountryCode() {
                return get(this.values, 'address.country.code', this.metadata.default_country_code);
            },
            countryOptions() {
                return [
                    ...this.countries.map((country) => ({
                        value: country,
                        text: country.name,
                    })),
                ];
            },
            stateOptions() {
                return [
                    { value: null, text: 'Please select state' },
                    ...this.states.map((state) => ({
                        value: state,
                        text: state.name,
                    })),
                ];
            },
            provinceOptions() {
                return [
                    { value: null, text: 'Please select province' },
                    ...this.provinces.map((province) => ({
                        value: province,
                        text: province.name,
                    })),
                ];
            },
        },
        data() {
            return {
                loading: false,
                countries: [],
                states: [],
                provinces: [],
                values: {},
            };
        },
        beforeMount() {
            if (this.$route.name === 'Work Order - Vendor Edit') {
                (async () => await this.loadVendor())();
            }
            this.values = this.getInitialValues({ address: {} });
        },
        mounted() {
            (async () => {
                this.loading = true;
                try {
                    await Promise.all([
                        this.fetchMetadata(),
                        this.fetchCountries(),
                        this.fetchStates(),
                        this.fetchProvinces(),
                    ]);

                    this.setDefaultCountry();
                    this.$refs.form.setValues(this.values);
                } finally {
                    this.loading = false;
                }
            })();
        },
        methods: {
            ...mapActions('workOrders', ['fetchVendor', 'updateVendor', 'createVendor']),
            goToVendorManager() {
                this.$router.push({ name: 'Work Order - Vendors' });
            },
            getAddressValue(address) {
                return {
                    address1: address.address1 || '',
                    address2: address.address2 || '',
                    country: address.country || null,
                    city: address.city || '',
                    state: address.state || null,
                    province: address.province || null,
                    zip: address.zip || '',
                    phone: address.phone || '',
                    work_phone: address.work_phone || '',
                    work_phone_ext: address.work_phone_ext || '',
                    fax: address.fax || '',
                };
            },
            getInitialValues(initialValues) {
                return {
                    id: initialValues.id || null,
                    first_name: initialValues.first_name || '',
                    last_name: initialValues.last_name || '',
                    email: initialValues.email || '',
                    company: initialValues.company || '',
                    specialty: initialValues.specialty || '',
                    address: this.getAddressValue(initialValues.address || {}),
                    mobile_phone: initialValues.mobile_phone || '',
                    note: initialValues.note || '',
                    tax_id: initialValues.tax_id || '',
                    pay_to: initialValues.pay_to || '',
                    send_email: initialValues.send_email || false,
                };
            },
            setDefaultCountry() {
                if (this.metadata.default_country_code && !this.values.address.country) {
                    const country = this.countries.find(
                        (country) => country.code === this.metadata.default_country_code
                    );
                    if (country) {
                        this.values.address.country = country;
                    }
                }
            },
            async handleZipChange(zip) {
                if (!this.values.address || !this.values.address.country || zip.length < 5) {
                    return;
                }

                this.loading = true;
                try {
                    const { data } = await API.get(`/asynchronous/ziptastic/${this.values.address.country.id}/${zip}`);
                    if (!data || data.message === 'Not Found') {
                        throw new Error();
                    }
                    this.values.address.city = data.city;
                    this.states.forEach((state) => {
                        if (state.code === data.state_short) {
                            this.values.address.state = state;
                        }
                    });
                } catch (err) {
                    this.warningToast({ text: 'Unable to auto fill city and state.' });
                } finally {
                    this.loading = false;
                }
            },
            async fetchCountries() {
                const getCountries = async (_) => {
                    const { data } = await API.get('/app/country/');
                    this.countries = data;
                };

                await this.dispatchRequest((_) => getCountries(), 'Could not load country data.');
            },
            async fetchStates() {
                const getStates = async (_) => {
                    const { data } = await API.get('/app/state/', {
                        params: {
                            country: this.values.address.country.id,
                        },
                    });
                    this.states = data;
                };

                if (this.values.address.country) {
                    await this.dispatchRequest((_) => getStates(), 'Could not load state data.', true);
                }
            },
            async fetchProvinces() {
                const getProvinces = async (_) => {
                    const { data } = await API.get('/app/province/', {
                        params: {
                            state: this.values.address.state.id,
                        },
                    });
                    this.provinces = data;
                };

                if (this.values.address.state) {
                    await this.dispatchRequest((_) => getProvinces(), 'Could not load province data.', true);
                }
            },
            async loadVendor() {
                await this.dispatchRequest(
                    (_) =>
                        this.fetchVendor({
                            id: this.$route.params.id,
                        }),
                    'Could not load vendor.'
                );
            },
            async fetchMetadata() {
                await this.dispatchRequest(
                    (_) => this.$store.dispatch('owner/fetchMetadata'),
                    'Could not load meta data.'
                );
            },
            async dispatchRequest(request, errorMessage = '', withLoader = false) {
                const toggleLoader = (loading) => {
                    if (withLoader) {
                        this.loading = loading;
                    }
                };

                try {
                    toggleLoader(true);
                    await request();
                } catch (err) {
                    VendorManagerEventBus.$emit('api-request-error', errorMessage);
                } finally {
                    toggleLoader(false);
                }
            },
            async onFormSubmit() {
                this.loading = true;
                try {
                    await (this.$route.params.id ? this.update() : this.create());
                    this.$router.push({
                        name: 'Work Order - Vendors',
                    });
                } catch (err) {
                    const { generic, fields } = formatResponseError(err);

                    if (generic) {
                        VendorManagerEventBus.$emit(
                            'api-request-error',
                            generic === 'Unknown Error' ? 'HTTP Request failed' : generic
                        );
                    }
                    if (!isEmpty(fields)) {
                        this.$refs.form.setErrors(fields);
                    }
                } finally {
                    this.loading = false;
                }
            },
            async update() {
                await this.updateVendor({
                    id: this.$route.params.id,
                    payload: this.values,
                });
            },
            async create() {
                await this.createVendor(this.values);
            },
        },
        watch: {
            'values.address.country': function (country, previousCountry) {
                const countryID = country && country.id;
                const previousCountryID = previousCountry && previousCountry.id;
                if (countryID !== previousCountryID) {
                    this.fetchStates();
                }
            },
            'values.address.state': function (state, previousState) {
                const stateID = state && state.id;
                const previousStateID = previousState && previousState.id;
                if (stateID !== previousStateID) {
                    this.fetchProvinces();
                }
            },
            vendor(newVendor) {
                this.values = this.getInitialValues(newVendor);
            },
        },
    };
</script>

<style scoped lang="scss"></style>
